import { useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { getBuilderCollectionContent } from '~/builder/utils/getBuilderCollectionContent';
import { useProductGridDefaultParameters } from '~/builder/utils/useProductGridDefaultParameters';
import { useAssortmentTest } from '~/pdp/context/AssortmentTestContext';
import { builderAutomaticSizeConfigValues } from '~/shared/constants/productSizes';
import { PRODUCTS_V2_RESULTS } from '~/shared/constants/queryCacheKeys';
import getTailgateHiddenBehaviorTagId from '~/shared/utils/getTailgateHiddenBehaviorTagId';
import { useCart } from '~/techstyle-shared/react-cart';

import { BROWSE_API_RESULTS_STALE_TIME } from '../constants';
import { normalizeProductsV2Response } from '../transformers/normalizeProductsV2Response';
import { combineCustomFacetFilters } from '../utils/filters';

import useGridInputs from './useGridInputs';

const useProductsV2 = () => {
  const productGridParameters = useProductGridDefaultParameters();
  const { builderBlock, excludeTagIdsToOmit } = useGridInputs();
  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const cart = useCart({
    isLoginRequired: true,
    params: {
      recalcCart: false,
      noCache: false,
      forceCreate: false,
      ignoreLinkedCart: true,
    },
  });

  const [customFacetFilters, setCustomFacetFilters] = useState({});
  const hiddenFacetFilters = combineCustomFacetFilters(customFacetFilters);

  const tailgateHiddenBehaviorTagId = getTailgateHiddenBehaviorTagId();

  // Originally we don't have excludeTagIds in the products v2 query, this is to follow the same logic as other places where we exclude the tailgate hidden behavior tag
  const baseExcludeTagIds = [];

  const shouldExcludeTailgateTagId = !excludeTagIdsToOmit?.includes(
    tailgateHiddenBehaviorTagId
  );

  const {
    excludeFplCodes: assortmentTestExcludeFplCodes,
    excludeTagIds: assortmentTestExcludeTagIds,
  } = useAssortmentTest();

  const productExcludeTagIds = [
    ...(shouldExcludeTailgateTagId ? [tailgateHiddenBehaviorTagId] : []),
    ...baseExcludeTagIds,
  ];

  const dynamicQueryParams = {
    ...productGridParameters,
    options: builderBlock?.component?.options,
    query,
    // IMPORTANT: Disable automatic size/gender filtering amd  here because it is handled in useAutoFilters hook
    // We need to ignore the automatic size filtering processing in getBuilderCollectionContent to avoid conflicts
    // until we can refactor the get API to a more simplified (less processed) query
    // Preference is to isolate the autofilter logic in the hook and update the url params accordingly
    // This will help keep the grid stateless and driven purely by url params
    automaticSizeFiltering: builderAutomaticSizeConfigValues.DISABLED,
    ignoreResolveSizeFilters: true,
    genderAutoFilterEnabled: false, // Add appropriate value or fetch dynamically
    // The following parameters do not seem to be used in getProductsV2Results
    // but are required by the function signature, temp set to null or false
    customerDetails: null, // Add appropriate value or fetch dynamically
    categoryNav: null, // Add appropriate value or fetch dynamically
    routeInfo: null, // Add appropriate value or fetch dynamically
    isTabGrid: false, // Add appropriate value or fetch dynamically
    apolloClient: null, // Add appropriate value or fetch dynamically
    hiddenFacetFilters, // TODO: integrate with the products/v2 API
    cart,
    productExcludeTagIds, // Exclude products with tags in excludeTagIds, such as the tailgate hidden behavior tag
    assortmentTestExcludeTagIds, // Exclude products with tags in assortmentTestExcludeTagIds, which are derived from active assortment test configurations
    assortmentTestExcludeFplCodes, // Exclude products with FPL codes in excludeFplCodes, which are derived from active assortment test configurations
  };

  const infiniteQueryResults = useInfiniteQuery({
    queryKey: [PRODUCTS_V2_RESULTS, dynamicQueryParams],
    queryFn: ({ pageParam = 1 }) => {
      // TODO: create simplified getProductsV2Results that directly doesn't have extraneous processing
      // return getProductsV2Results({
      //   page: pageParam,
      //   ...dynamicQueryParams,
      // });
      return getBuilderCollectionContent({
        page: pageParam,
        ...dynamicQueryParams,
      });
    },
    /**
     * Memoize the select transform to ensure React Query only re-runs the transformation
     * when the actual data changes, not on every component re-render.
     *
     * Without useMemo, a new function would be created on each render, causing React Query
     * to think the select function changed and re-execute the transformation unnecessarily.
     *
     * The empty dependency array [] ensures the function reference remains stable across
     * renders, while React Query's internal caching handles calling it only when new data
     * is fetched from the API.
     */
    select: useMemo(
      () => (response) => {
        return normalizeProductsV2Response(response);
      },
      []
    ),
    staleTime: BROWSE_API_RESULTS_STALE_TIME,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextPage;
    },
    // We need keep previous data to be true so that we can show the disabled options state
    keepPreviousData: true,
  });

  return {
    ...infiniteQueryResults,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useProductsV2;
